/**
 * Endpoint schemas (§7.2).
 *
 * Phase 0 and Phase 1 surface, plus the item mutation because its `version` field is a
 * contract decision (ADR 0005) rather than an implementation detail. Later endpoints are
 * added here as they are built; the OpenAPI document is generated from these, never
 * hand-written.
 */

import { z } from 'zod';

import {
  auditStatusSchema,
  categoryScopeSchema,
  checklistCategorySchema,
  contextProfileSchema,
  criticalitySchema,
  cuid,
  dataClassificationSchema,
  evidenceKindSchema,
  findingStatusSchema,
  evidenceSystemSchema,
  exposureSchema,
  freshnessStatusSchema,
  isoDateTime,
  itemRef,
  itemScopeSchema,
  itemStateSchema,
  maturityLevelSchema,
  obligationSchema,
  orgRoleSchema,
  resultOriginSchema,
  severitySchema,
  teamRoleSchema,
  waiverStatusSchema,
} from './primitives.js';

// ── GET /me ───────────────────────────────────────────────────────────────────

export const meProjectMembershipSchema = z.object({
  projectId: cuid,
  projectName: z.string(),
  roles: z.array(teamRoleSchema).min(1),
  acknowledgedAt: isoDateTime.nullable(),
});

export const meSchema = z.object({
  id: cuid,
  displayName: z.string(),
  email: z.string().email(),
  initials: z.string().min(1).max(3),
  orgRole: orgRoleSchema,
  /** Which Entra group produced the org role — shown read-only on the profile (§8.2). */
  orgRoleSource: z.string().nullable(),
  memberships: z.array(meProjectMembershipSchema),
  /** True only in `local` with the dev bypass on. The UI shows a banner (§15 risk 2). */
  isDevSession: z.boolean(),
});

export type Me = z.infer<typeof meSchema>;

// ── GET /users ────────────────────────────────────────────────────────────────

/** Enough to populate a member or owner picker. Not the admin user record. */
export const userSummarySchema = z.object({
  id: cuid,
  displayName: z.string(),
  email: z.string().email(),
  initials: z.string().min(1).max(3),
  orgRole: orgRoleSchema,
});

export type UserSummary = z.infer<typeof userSummarySchema>;

// ── GET /checklist/current ────────────────────────────────────────────────────

/**
 * The published template, sent to the browser so the registration wizard can compute
 * applicability **with the same engine the server uses** (ADR 0003).
 *
 * This is the payload that makes `WizardContext`'s live scope panel possible without a
 * round-trip per toggle: `contextCondition` arrives as source text and the client parses
 * it with `parseContextCondition` from `@portal/domain`.
 */
export const checklistItemSchema = z.object({
  ref: itemRef,
  section: z.string().length(1),
  subsection: z.string(),
  sortOrder: z.number().int(),
  statement: z.string(),
  evidenceExpectation: z.string().nullable(),
  obligation: obligationSchema,
  contextCondition: z.string().nullable(),
  scope: itemScopeSchema,
  primaryRepoOnly: z.boolean(),
  freshnessDays: z.number().int(),
  inheritable: z.boolean(),
  ownerRole: teamRoleSchema,
  category: checklistCategorySchema.nullable(),
  autoCheckId: z.string().nullable(),
});

export const currentChecklistSchema = z.object({
  version: z.string(),
  publishedAt: isoDateTime.nullable(),
  items: z.array(checklistItemSchema),
  gateQuestions: z.array(
    z.object({
      number: z.number().int(),
      question: z.string(),
      itemRefs: z.array(itemRef),
    }),
  ),
  /** Items with no authored evidence expectation — reported, not hidden (ADR 0009). */
  missingEvidenceExpectation: z.number().int().nonnegative(),
});

export type CurrentChecklist = z.infer<typeof currentChecklistSchema>;

// ── GitLab browsing (§9.1) ────────────────────────────────────────────────────

/** Read-only projections of GitLab groups and projects. Nothing here is ever written. */
export const gitlabGroupSchema = z.object({
  id: z.string(),
  fullPath: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

export const gitlabProjectSchema = z.object({
  id: z.string(),
  pathWithNamespace: z.string(),
  name: z.string(),
  defaultBranch: z.string(),
  visibility: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
});

export type GitlabGroup = z.infer<typeof gitlabGroupSchema>;
export type GitlabProject = z.infer<typeof gitlabProjectSchema>;

// ── Scores, shared by several read models ─────────────────────────────────────

export const scoreFigureSchema = z.object({
  applicable: z.number().int().nonnegative(),
  verified: z.number().int().nonnegative(),
  /** null when nothing applicable is in scope. Rendered "—", never 0% (§6.4). */
  percent: z.number().nullable(),
  percentRounded: z.number().int().nullable(),
});

export const sectionScoreSchema = z.object({
  section: z.string().length(1),
  mandatory: scoreFigureSchema,
  recommended: scoreFigureSchema,
  counts: z.object({
    verified: z.number().int(),
    partial: z.number().int(),
    rejected: z.number().int(),
    pending: z.number().int(),
    waived: z.number().int(),
    notApplicable: z.number().int(),
  }),
});

export const scoreBreakdownSchema = z.object({
  sections: z.array(sectionScoreSchema),
  mandatory: scoreFigureSchema,
  recommended: scoreFigureSchema,
  waiverAdjusted: scoreFigureSchema,
  maturity: maturityLevelSchema.nullable(),
  notApplicableByContext: z.number().int().nonnegative(),
  verifiedByAttestation: z.number().int().nonnegative(),
});

/** A published score is always accompanied by the audit it came from (§6.4). */
export const scoreAuthoritySchema = z.object({
  auditId: cuid,
  auditNumber: z.number().int(),
  closedAt: isoDateTime,
  breakdown: scoreBreakdownSchema,
});

// ── GET /projects ─────────────────────────────────────────────────────────────

export const portfolioRowSchema = z.object({
  id: cuid,
  name: z.string(),
  clientName: z.string(),
  repositoryCount: z.number().int().nonnegative(),
  mandatoryPercent: z.number().nullable(),
  maturity: maturityLevelSchema.nullable(),
  gatePassing: z.number().int().nullable(),
  gateTotal: z.number().int().nullable(),
  lastAudit: z
    .object({ id: cuid, number: z.number().int(), closedAt: isoDateTime })
    .nullable(),
  nextAudit: z
    .object({ dueDate: isoDateTime, overdueDays: z.number().int().nonnegative() })
    .nullable(),
  openFindings: z.number().int().nonnegative(),
  overdueFindings: z.number().int().nonnegative(),
  auditorInitials: z.array(z.string()),
});

export const portfolioSchema = z.object({
  rows: z.array(portfolioRowSchema),
  totals: z.object({
    projects: z.number().int(),
    averageMandatoryPercent: z.number().nullable(),
    gateReady: z.number().int(),
    overdueFindings: z.number().int(),
    overdueAudits: z.number().int(),
  }),
  checklistVersion: z.string(),
  nextCursor: z.string().nullable(),
});

export type Portfolio = z.infer<typeof portfolioSchema>;

// ── GET /projects/:id — the overview payload (§7.3) ───────────────────────────

/**
 * One request, everything `ProjectOverview.dc.html` renders (§7.3).
 *
 * `score.published` is the authority — the snapshot of the latest closed audit, read back
 * rather than recomputed (ADR 0004). `score.live` is the in-progress projection, computed
 * by the same engine and always labelled as a projection. Both are null on a project that
 * has never been audited, which is not the same as scoring zero.
 */
export const projectOverviewSchema = z.object({
  project: z.object({
    id: cuid,
    name: z.string(),
    clientName: z.string(),
    criticality: criticalitySchema,
    dataClassification: dataClassificationSchema,
    exposure: exposureSchema,
    hostingRegion: z.string(),
    status: z.enum(['ACTIVE', 'ARCHIVED']),
    gitlabGroupId: z.string().nullable(),
    createdAt: isoDateTime,
  }),
  context: contextProfileSchema,
  contextHistory: z.array(
    z.object({
      changedAt: isoDateTime,
      changedBy: z.string(),
      justification: z.string(),
      entering: z.number().int().nonnegative(),
      leaving: z.number().int().nonnegative(),
    }),
  ),
  /** Which categories the project covers. `'ALL'` unless someone has narrowed it. */
  categoryScope: categoryScopeSchema,
  categoryScopeHistory: z.array(
    z.object({
      changedAt: isoDateTime,
      changedBy: z.string(),
      justification: z.string(),
      after: categoryScopeSchema,
      entering: z.number().int().nonnegative(),
      leaving: z.number().int().nonnegative(),
    }),
  ),
  /**
   * What this project's context profile and category scope bring into scope, from the
   * pinned template.
   */
  scope: z.object({
    checklistVersion: z.string(),
    total: z.number().int().nonnegative(),
    applicable: z.number().int().nonnegative(),
    mandatory: z.number().int().nonnegative(),
    recommended: z.number().int().nonnegative(),
    notApplicable: z.number().int().nonnegative(),
    missingEvidenceExpectation: z.number().int().nonnegative(),
    sections: z.array(
      z.object({
        section: z.string().length(1),
        applicable: z.number().int().nonnegative(),
        mandatory: z.number().int().nonnegative(),
      }),
    ),
  }),
  score: z.object({
    published: scoreAuthoritySchema.nullable(),
    live: z
      .object({
        auditId: cuid,
        auditNumber: z.number().int(),
        status: auditStatusSchema,
        breakdown: scoreBreakdownSchema,
      })
      .nullable(),
  }),
  repositories: z.array(
    z.object({
      id: cuid,
      pathWithNamespace: z.string(),
      defaultBranch: z.string(),
      isPrimary: z.boolean(),
      lastActivityAt: isoDateTime.nullable(),
    }),
  ),
  members: z.array(
    z.object({
      /**
       * The ProjectMember row's id, not the user's.
       *
       * One person can hold two roles, so `userId` does not identify an assignment —
       * which is what PATCH and DELETE /projects/:id/members/:memberId address.
       */
      id: cuid,
      userId: cuid,
      displayName: z.string(),
      initials: z.string(),
      role: teamRoleSchema,
      isBackup: z.boolean(),
      acknowledgedAt: isoDateTime.nullable(),
    }),
  ),
  audits: z.array(
    z.object({
      id: cuid,
      number: z.number().int(),
      typeName: z.string(),
      periodLabel: z.string(),
      status: auditStatusSchema,
      dueDate: isoDateTime.nullable(),
      closedAt: isoDateTime.nullable(),
      mandatoryPercent: z.number().nullable(),
    }),
  ),
  findings: z.object({
    open: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    critical: z.number().int().nonnegative(),
  }),
  activity: z.array(
    z.object({
      at: isoDateTime,
      action: z.string(),
      actor: z.string().nullable(),
      entityType: z.string(),
    }),
  ),
});

export type ProjectOverview = z.infer<typeof projectOverviewSchema>;

/** `PATCH /projects/:id` — identity only. Context has its own endpoint and its own record. */
export const patchProjectSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  clientName: z.string().min(2).max(120).optional(),
  criticality: criticalitySchema.optional(),
  dataClassification: dataClassificationSchema.optional(),
  exposure: exposureSchema.optional(),
  hostingRegion: z.string().min(2).max(60).optional(),
  gitlabGroupId: z.string().nullable().optional(),
});

export type PatchProjectInput = z.infer<typeof patchProjectSchema>;

// ── Team: POST / PATCH / DELETE /projects/:id/members ─────────────────────────

/**
 * One assignment: a person, in a role, primary or backup.
 *
 * A row per (person, role) pair is what `ProjectMember`'s unique constraint models — so
 * someone who is both Tech Lead and Security Lead holds two rows rather than one row
 * carrying two roles. That is deliberate: `acknowledgedAt` is per assignment, and
 * acknowledging one role is not acknowledging the other.
 */
export const assignMemberSchema = z.object({
  userId: cuid,
  role: teamRoleSchema,
  isBackup: z.boolean().default(false),
});

export type AssignMemberInput = z.infer<typeof assignMemberSchema>;

/**
 * Changing an existing assignment.
 *
 * Either field moves the assignment to a different person or a different role, and both
 * clear `acknowledgedAt` — an owner who has not acknowledged is not an owner
 * (`TeamOwnership.dc.html`), so a reassignment cannot inherit the previous holder's
 * acknowledgement.
 */
export const patchMemberSchema = z
  .object({
    userId: cuid.optional(),
    role: teamRoleSchema.optional(),
    isBackup: z.boolean().optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Nothing to change.',
  });

export type PatchMemberInput = z.infer<typeof patchMemberSchema>;

// ── POST /projects ────────────────────────────────────────────────────────────

export const createProjectSchema = z.object({
  name: z.string().min(2).max(120),
  clientName: z.string().min(2).max(120),
  criticality: criticalitySchema,
  dataClassification: dataClassificationSchema,
  exposure: exposureSchema,
  hostingRegion: z.string().min(2).max(60),
  gitlabGroupId: z.string().nullable(),
  context: contextProfileSchema,
  repositories: z
    .array(
      z.object({
        gitlabProjectId: z.string().min(1),
        pathWithNamespace: z.string().min(1),
        defaultBranch: z.string().min(1),
        isPrimary: z.boolean(),
      }),
    )
    .min(1, 'A project needs at least one repository'),
  members: z
    .array(z.object({ userId: cuid, role: teamRoleSchema, isBackup: z.boolean().default(false) }))
    .min(1),
});

export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/** What `POST /projects` returns: enough to navigate to the project it just made. */
export const createdProjectSchema = z.object({
  id: cuid,
  name: z.string(),
  clientName: z.string(),
  repositoryCount: z.number().int().nonnegative(),
  memberCount: z.number().int().nonnegative(),
  /** Computed from the context profile against the published template. */
  scope: z.object({
    applicable: z.number().int().nonnegative(),
    mandatory: z.number().int().nonnegative(),
    notApplicable: z.number().int().nonnegative(),
    checklistVersion: z.string(),
  }),
});

export type CreatedProject = z.infer<typeof createdProjectSchema>;

// ── PUT /projects/:id/context ─────────────────────────────────────────────────

export const contextChangeSchema = z.object({
  context: contextProfileSchema,
  /** Required to commit; ignored for a dry run. The diff is the write path (§7.2). */
  justification: z.string().min(10).optional(),
});

export const applicabilityDiffSchema = z.object({
  entering: z.array(itemRef),
  leaving: z.array(itemRef),
  totals: z.object({
    before: z.object({ applicable: z.number().int(), mandatory: z.number().int() }),
    after: z.object({ applicable: z.number().int(), mandatory: z.number().int() }),
  }),
});

/**
 * What `PUT /projects/:id/context` returns, dry-run or committed.
 *
 * The mandatory lists are separated because they are the ones that move the score — a
 * recommended item entering scope changes coverage, a mandatory one changes the number a
 * client reads.
 */
export const contextChangeResultSchema = z.object({
  dryRun: z.boolean(),
  diff: applicabilityDiffSchema,
  mandatoryEntering: z.array(itemRef),
  mandatoryLeaving: z.array(itemRef),
});

export type ContextChangeResult = z.infer<typeof contextChangeResultSchema>;

// ── PUT /projects/:id/category-scope ──────────────────────────────────────────

export const categoryScopeChangeSchema = z.object({
  categories: categoryScopeSchema,
  /** Required to commit; ignored for a dry run. Same rule as a context change. */
  justification: z.string().min(10).optional(),
});

export type CategoryScopeChangeInput = z.infer<typeof categoryScopeChangeSchema>;

/**
 * What `PUT /projects/:id/category-scope` returns, dry-run or committed. The diff has the
 * context diff's shape, so the confirmation panel renders both the same way.
 */
export const categoryScopeChangeResultSchema = z.object({
  dryRun: z.boolean(),
  diff: applicabilityDiffSchema,
  mandatoryEntering: z.array(itemRef),
  mandatoryLeaving: z.array(itemRef),
});

export type CategoryScopeChangeResult = z.infer<typeof categoryScopeChangeResultSchema>;

// ── POST /audits/preview ──────────────────────────────────────────────────────

export const inheritancePreviewRequestSchema = z.object({
  projectId: cuid,
  auditTypeId: cuid,
  periodLabel: z.string().min(2).max(40),
  dueDate: isoDateTime.nullable(),
  inheritFromAuditId: cuid.nullable(),
});

export const inheritancePreviewSchema = z.object({
  counts: z.object({
    inScope: z.number().int(),
    mandatory: z.number().int(),
    recommended: z.number().int(),
    inherited: z.number().int(),
    toVerify: z.number().int(),
    mustRecheck: z.number().int(),
  }),
  inherited: z.array(itemRef),
  toVerify: z.array(itemRef),
  mustRecheck: z.array(
    z.object({
      ref: itemRef,
      reason: z.enum(['PROOF_OF_EXECUTION', 'EVIDENCE_EXPIRED', 'PREVIOUS_STATE_NOT_CARRIED']),
      previousState: itemStateSchema,
    }),
  ),
});

export type InheritancePreview = z.infer<typeof inheritancePreviewSchema>;

// ── POST /audits ──────────────────────────────────────────────────────────────

export const auditTypeSchema = z.object({
  id: cuid,
  key: z.string(),
  name: z.string(),
  description: z.string(),
  /** Empty = the whole checklist. Otherwise the refs this type covers. */
  scopeRefs: z.array(itemRef),
});

export type AuditType = z.infer<typeof auditTypeSchema>;

export const createAuditSchema = z.object({
  projectId: cuid,
  auditTypeId: cuid,
  periodLabel: z.string().min(2).max(40),
  dueDate: isoDateTime.nullable(),
  /** null = inherit from the project's latest closed audit; otherwise an explicit one. */
  inheritFromAuditId: cuid.nullable(),
  assignedAuditorIds: z.array(cuid).default([]),
});

export type CreateAuditInput = z.infer<typeof createAuditSchema>;

export const createdAuditSchema = z.object({
  id: cuid,
  number: z.number().int(),
  projectId: cuid,
  itemResults: z.number().int().nonnegative(),
  repoResults: z.number().int().nonnegative(),
  counts: inheritancePreviewSchema.shape.counts,
});

export type CreatedAudit = z.infer<typeof createdAuditSchema>;

// ── GET /audits/:id/review ────────────────────────────────────────────────────

/**
 * The blocking-rule list (§7.2). Computed server-side and **never re-derived in the
 * client** — the review screen shows what the server will actually enforce on submit.
 */
export const reviewSchema = z.object({
  auditId: cuid,
  status: auditStatusSchema,
  canSubmit: z.boolean(),
  blocking: z.array(
    z.object({
      rule: z.string(),
      detail: z.string(),
      refs: z.array(itemRef),
    }),
  ),
  advisory: z.array(
    z.object({
      rule: z.string(),
      detail: z.string(),
      refs: z.array(itemRef),
    }),
  ),
  /** What the score would be if it were closed as it stands. */
  projected: scoreBreakdownSchema,
  resolution: z.object({
    inScope: z.number().int(),
    resolved: z.number().int(),
    pending: z.number().int(),
  }),
});

export type Review = z.infer<typeof reviewSchema>;

// ── POST /audits/:id/approve ──────────────────────────────────────────────────

/**
 * The frozen result. `snapshot` is written once and never recomputed (ADR 0004), so this
 * is the only response in the API whose numbers are guaranteed never to change.
 */
export const approvedAuditSchema = z.object({
  id: cuid,
  number: z.number().int(),
  closedAt: isoDateTime,
  snapshot: scoreBreakdownSchema,
});

export type ApprovedAudit = z.infer<typeof approvedAuditSchema>;

// ── POST /audits/:id/cancel ───────────────────────────────────────────────────

export const cancelAuditSchema = z.object({
  reason: z.string().trim().min(1).max(1000).nullable().default(null),
});

export type CancelAuditInput = z.infer<typeof cancelAuditSchema>;

export const cancelledAuditSchema = z.object({
  id: cuid,
  number: z.number().int(),
  status: z.literal('CANCELLED'),
});

export type CancelledAudit = z.infer<typeof cancelledAuditSchema>;

// ── GET /audits/:id/matrix ────────────────────────────────────────────────────

export const matrixSchema = z.object({
  auditId: cuid,
  repositories: z.array(
    z.object({ id: cuid, pathWithNamespace: z.string(), isPrimary: z.boolean() }),
  ),
  rows: z.array(
    z.object({
      ref: itemRef,
      statement: z.string(),
      obligation: obligationSchema,
      isContextual: z.boolean(),
      primaryRepoOnly: z.boolean(),
      rollUp: z.object({
        state: itemStateSchema,
        verified: z.number().int(),
        considered: z.number().int(),
        label: z.string(),
      }),
      cells: z.array(
        z.object({
          repositoryId: cuid,
          state: itemStateSchema.nullable(),
          gapNote: z.string().nullable(),
        }),
      ),
    }),
  ),
});

export type Matrix = z.infer<typeof matrixSchema>;

// ── PATCH /audits/:id/items/:ref ──────────────────────────────────────────────

export const patchItemSchema = z.object({
  /** Required, not optional — a mutation without it cannot be safe (ADR 0005). */
  version: z.number().int().nonnegative(),
  state: itemStateSchema.optional(),
  gapNote: z.string().max(2000).nullable().optional(),
  naJustification: z.string().max(2000).nullable().optional(),
  /**
   * The auditor's own confirmation, for items with no artefact to link (ADR 0008). A
   * verification needs an evidence record or this.
   */
  attestation: z.string().max(2000).nullable().optional(),
  assignedToUserId: cuid.nullable().optional(),
  recordedValues: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});

export const evidenceSchema = z.object({
  id: cuid,
  kind: evidenceKindSchema,
  system: evidenceSystemSchema,
  url: z.string().url().nullable(),
  label: z.string(),
  capturedAt: isoDateTime,
  capturedBy: z.object({ id: cuid, displayName: z.string(), initials: z.string() }),
  lastCheckedAt: isoDateTime.nullable(),
  lastCheckStatus: z.enum(['REACHABLE', 'UNREACHABLE', 'UNCHECKED']),
  fromProbe: z.boolean(),
});

export const addEvidenceSchema = z.object({
  kind: evidenceKindSchema,
  system: evidenceSystemSchema,
  url: z.string().url().nullable(),
  label: z.string().min(2).max(200),
});

// ── GET /audits/:id/evidence — the evidence catalog (issue #4) ───────────────

/**
 * One item that has something proving it: at least one evidence record, or an
 * attestation (ADR 0008). Items with neither are not on this list — the catalog is
 * about what has been proven, not all 218 items.
 */
export const evidenceCatalogRowSchema = z.object({
  ref: itemRef,
  section: z.string().length(1),
  obligation: obligationSchema,
  statement: z.string(),
  state: itemStateSchema,
  attestation: z.string().nullable(),
  evidence: z.array(evidenceSchema),
});

export const evidenceCatalogSchema = z.object({
  auditId: cuid,
  rows: z.array(evidenceCatalogRowSchema),
  totals: z.object({
    evidenceCount: z.number().int().nonnegative(),
    /** ADR 0008's disclosure: how many verifications rest on a statement, not an artefact. */
    attestedItems: z.number().int().nonnegative(),
    itemsWithProof: z.number().int().nonnegative(),
    inScope: z.number().int().nonnegative(),
  }),
});

export type EvidenceCatalogRow = z.infer<typeof evidenceCatalogRowSchema>;
export type EvidenceCatalog = z.infer<typeof evidenceCatalogSchema>;

// ── GET /audits/:id — the workspace payload (§7.3) ────────────────────────────

/**
 * One item row, carrying its own drawer payload.
 *
 * §7.3: "the full drawer payload for the focused item — the drawer never fetches
 * separately". Sending it per row rather than per focus keeps opening an item instant,
 * which is the difference between a tool and a form when someone works through 218 of
 * them.
 */
export const workspaceItemRowSchema = z.object({
  ref: itemRef,
  section: z.string().length(1),
  subsection: z.string(),
  obligation: obligationSchema,
  isContextual: z.boolean(),
  statement: z.string(),
  /** null = nobody has written one yet (ADR 0009). The drawer says so explicitly. */
  evidenceExpectation: z.string().nullable(),
  scope: itemScopeSchema,
  primaryRepoOnly: z.boolean(),
  freshnessDays: z.number().int(),
  inheritable: z.boolean(),
  state: itemStateSchema,
  gapNote: z.string().nullable(),
  naJustification: z.string().nullable(),
  attestation: z.string().nullable(),
  origin: resultOriginSchema,
  inheritedFromAuditNumber: z.number().int().nullable(),
  nonInheritableReason: z
    .enum(['PROOF_OF_EXECUTION', 'EVIDENCE_EXPIRED', 'PREVIOUS_STATE_NOT_CARRIED'])
    .nullable(),
  version: z.number().int(),
  evidenceCount: z.number().int(),
  evidence: z.array(evidenceSchema),
  /** True when this verification rests on an attestation rather than an artefact. */
  attested: z.boolean(),
  owner: z.object({ id: cuid, initials: z.string(), displayName: z.string() }).nullable(),
  rollUp: z
    .object({
      state: itemStateSchema,
      verified: z.number().int(),
      considered: z.number().int(),
      label: z.string(),
    })
    .nullable(),
  repoResults: z.array(
    z.object({
      repositoryId: cuid,
      pathWithNamespace: z.string(),
      isPrimary: z.boolean(),
      state: itemStateSchema,
      gapNote: z.string().nullable(),
    }),
  ),
  recordedValueFields: z.array(
    z.object({ label: z.string(), unit: z.string(), type: z.string() }),
  ),
  recordedValues: z.array(z.object({ label: z.string(), value: z.string(), unit: z.string() })),
  probe: z
    .object({
      status: z.enum(['PASS', 'FAIL', 'INDETERMINATE']),
      runAt: isoDateTime,
      diverges: z.boolean(),
    })
    .nullable(),
  freshness: z
    .object({ status: freshnessStatusSchema, expiresAt: isoDateTime, daysRemaining: z.number().int() })
    .nullable(),
});

export const auditWorkspaceSchema = z.object({
  audit: z.object({
    id: cuid,
    number: z.number().int(),
    projectId: cuid,
    projectName: z.string(),
    typeName: z.string(),
    periodLabel: z.string(),
    status: auditStatusSchema,
    dueDate: isoDateTime.nullable(),
    templateVersion: z.string(),
    readOnly: z.boolean(),
    readOnlyReason: z.string().nullable(),
  }),
  progress: z.object({
    verified: z.number().int(),
    partial: z.number().int(),
    rejected: z.number().int(),
    pending: z.number().int(),
    waived: z.number().int(),
    notApplicable: z.number().int(),
    inScope: z.number().int(),
  }),
  sections: z.array(
    z.object({
      section: z.string().length(1),
      title: z.string(),
      resolved: z.number().int(),
      inScope: z.number().int(),
      hasRejected: z.boolean(),
    }),
  ),
  section: z.string().length(1),
  items: z.array(workspaceItemRowSchema),
});

export type AuditWorkspace = z.infer<typeof auditWorkspaceSchema>;

// ── GET /projects/:id/gate ────────────────────────────────────────────────────

export const gateRowSchema = z.object({
  number: z.number().int().min(1).max(22),
  question: z.string(),
  itemRefs: z.array(itemRef),
  passing: z.boolean(),
  failing: z.array(
    z.object({
      ref: itemRef,
      state: itemStateSchema.nullable(),
      reason: z.enum([
        'PENDING',
        'PARTIAL',
        'REJECTED',
        'WAIVER_NOT_APPROVED',
        'WAIVER_EXPIRED',
        'MISSING_RESULT',
      ]),
    }),
  ),
});

export const gateSchema = z.object({
  rows: z.array(gateRowSchema),
  passing: z.number().int(),
  total: z.number().int(),
  ready: z.boolean(),
  signOffs: z.array(
    z.object({
      role: teamRoleSchema,
      holder: z.object({ id: cuid, displayName: z.string() }).nullable(),
      signedAt: isoDateTime.nullable(),
      blockedByRefs: z.array(itemRef),
    }),
  ),
});

export type Gate = z.infer<typeof gateSchema>;

// ── KPI readings (ADR 0010) ───────────────────────────────────────────────────
//
// The eight G-11 figures the portal cannot compute. `sourceSystem` is required and
// `recordedBy`/`recordedAt` are server-set — a figure without provenance is the thing
// this product exists to prevent.

export const kpiMetricSchema = z.enum([
  'AVAILABILITY',
  'SLA_ATTAINMENT',
  'OPEN_CRITICAL_HIGH_VULNS',
  'MTTP_CRITICAL_CVE',
  'COVERAGE_NEW_CODE',
  'QUALITY_GATE_PASS_RATE',
  'BACKUP_SUCCESS_RATE',
  'INCIDENTS_BY_SEVERITY',
]);

export const kpiVerdictSchema = z.enum(['ON_TARGET', 'OFF_TARGET', 'NO_TARGET']);
export const kpiCadenceSchema = z.enum(['MONTHLY', 'QUARTERLY']);

/** `2026-09` for a monthly metric, `2026-Q3` for a quarterly one. */
export const kpiPeriod = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2]|Q[1-4])$/, 'Period must be YYYY-MM or YYYY-Qn');

// ── PUT /projects/:id/kpi ─────────────────────────────────────────────────────

export const upsertKpiReadingSchema = z.object({
  metric: kpiMetricSchema,
  period: kpiPeriod,
  value: z.number().finite(),
  unit: z.string().min(1).max(16),
  /** Omit to inherit the project's target; null to record without one. */
  target: z.number().finite().nullable().optional(),
  sourceSystem: evidenceSystemSchema,
  sourceUrl: z.string().url().max(2000).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
});

export const kpiReadingSchema = z.object({
  metric: kpiMetricSchema,
  period: kpiPeriod,
  value: z.number(),
  unit: z.string(),
  target: z.number().nullable(),
  verdict: kpiVerdictSchema,
  sourceSystem: evidenceSystemSchema,
  sourceUrl: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.object({ id: cuid, displayName: z.string(), initials: z.string() }),
  recordedAt: isoDateTime,
});

/** One tile. `latest` is null when nobody has ever recorded this metric. */
export const kpiTileSchema = z.object({
  metric: kpiMetricSchema,
  label: z.string(),
  unit: z.string(),
  cadence: kpiCadenceSchema,
  direction: z.enum(['HIGHER_IS_BETTER', 'LOWER_IS_BETTER']),
  precision: z.number().int(),
  expectedSource: evidenceSystemSchema,
  latest: kpiReadingSchema.nullable(),
  verdict: kpiVerdictSchema,
  missingForPeriod: z.boolean(),
  asOfEarlierPeriod: kpiPeriod.nullable(),
  /** Oldest first, for the sparkline. Capped server-side. */
  history: z.array(z.object({ period: kpiPeriod, value: z.number(), target: z.number().nullable() })),
});

/**
 * The two figures the portal derives itself. Separated from the recorded eight so the
 * screen can label provenance structurally rather than in prose.
 */
export const computedKpiSchema = z.object({
  metric: z.enum(['OPEN_FINDINGS_PAST_DEADLINE', 'ACTUAL_RTO_RPO']),
  label: z.string(),
  value: z.number().nullable(),
  unit: z.string(),
  target: z.number().nullable(),
  verdict: kpiVerdictSchema,
  /** What it was computed from, so the number can be traced. */
  basis: z.string(),
});

export const kpiDashboardSchema = z.object({
  projectId: cuid,
  projectName: z.string(),
  period: kpiPeriod,
  computed: z.array(computedKpiSchema),
  recorded: z.array(kpiTileSchema),
  /** How many of the eight are due this period and not yet entered. */
  missingCount: z.number().int(),
  /** Score trend and maturity spread — genuinely the portal's own numbers. */
  scoreTrend: z.array(z.object({ auditNumber: z.number().int(), percent: z.number() })),
});

export const kpiTargetsSchema = z.object({
  targets: z.array(
    z.object({ metric: kpiMetricSchema, target: z.number().finite().nullable(), unit: z.string() }),
  ),
});

export type KpiMetricName = z.infer<typeof kpiMetricSchema>;
export type UpsertKpiReading = z.infer<typeof upsertKpiReadingSchema>;
export type KpiReadingDto = z.infer<typeof kpiReadingSchema>;
export type KpiTile = z.infer<typeof kpiTileSchema>;
export type KpiDashboard = z.infer<typeof kpiDashboardSchema>;
export type KpiTargets = z.infer<typeof kpiTargetsSchema>;

// ── Findings register (§9.12, Phase 3) ────────────────────────────────────────

export const findingSchema = z.object({
  id: cuid,
  reference: z.string(),
  title: z.string(),
  gapDescription: z.string(),
  remediation: z.string(),
  severity: severitySchema,
  status: findingStatusSchema,
  itemRef: itemRef,
  projectId: cuid,
  projectName: z.string(),
  auditNumber: z.number().int(),
  owner: z.object({ id: cuid, displayName: z.string(), initials: z.string() }).nullable(),
  raisedBy: z.object({ id: cuid, displayName: z.string(), initials: z.string() }),
  raisedAt: isoDateTime,
  deadline: isoDateTime.nullable(),
  closedAt: isoDateTime.nullable(),
  /** Computed server-side from the severity ladder — never re-derived in the client. */
  daysRemaining: z.number().int(),
  pastDeadline: z.boolean(),
  ageDays: z.number().int(),
  extensions: z.array(
    z.object({
      previousDeadline: isoDateTime,
      newDeadline: isoDateTime,
      reason: z.string(),
      requestedAt: isoDateTime,
    }),
  ),
});

export const findingsSchema = z.object({
  findings: z.array(findingSchema),
  counts: z.object({
    open: z.number().int(),
    inProgress: z.number().int(),
    closed: z.number().int(),
    waived: z.number().int(),
    /** KPI G-11's loudest number. */
    pastDeadline: z.number().int(),
  }),
});

export const createFindingSchema = z.object({
  projectId: cuid,
  auditId: cuid,
  itemRef: itemRef,
  title: z.string().min(4).max(200),
  gapDescription: z.string().min(4).max(4000),
  remediation: z.string().min(4).max(4000),
  severity: severitySchema,
  ownerUserId: cuid.nullable(),
  /** Omit to take the severity's own deadline, which is the intended path. */
  deadline: isoDateTime.nullable().optional(),
});

export const patchFindingSchema = z.object({
  status: findingStatusSchema.optional(),
  severity: severitySchema.optional(),
  ownerUserId: cuid.nullable().optional(),
  remediation: z.string().min(4).max(4000).optional(),
  /** Extending a deadline always needs a reason; the extension is kept in the trail. */
  newDeadline: isoDateTime.optional(),
  extensionReason: z.string().min(4).max(1000).optional(),
});

export type Finding = z.infer<typeof findingSchema>;
export type Findings = z.infer<typeof findingsSchema>;
export type CreateFindingInput = z.infer<typeof createFindingSchema>;
export type PatchFindingInput = z.infer<typeof patchFindingSchema>;

// ── Waivers (§9.15, Phase 3) ──────────────────────────────────────────────────

export const waiverEffectSchema = z.enum([
  'COVERING',
  'EXPIRED',
  'EXPIRING_SOON',
  'NOT_COVERING',
]);

export const waiverSchema = z.object({
  id: cuid,
  itemRef: itemRef,
  statement: z.string(),
  reason: z.string(),
  status: waiverStatusSchema,
  /** What it is doing today. A half-signed waiver covers nothing. */
  effect: waiverEffectSchema,
  requestedBy: z.object({ id: cuid, displayName: z.string(), initials: z.string() }),
  requestedAt: isoDateTime,
  expiresAt: isoDateTime.nullable(),
  daysUntilExpiry: z.number().int().nullable(),
  signatures: z.array(
    z.object({
      role: teamRoleSchema,
      user: z.object({ id: cuid, displayName: z.string(), initials: z.string() }),
      signedAt: isoDateTime,
    }),
  ),
  /** Whether the caller may add the outstanding signature, and why not if they may not. */
  canSign: z.boolean(),
  cannotSignReason: z.string().nullable(),
});

export const waiversSchema = z.object({
  waivers: z.array(waiverSchema),
  /** Items excluded by the context profile — adjacent on the screen, different in kind. */
  notApplicable: z.array(
    z.object({
      itemRef: itemRef,
      statement: z.string(),
      justification: z.string(),
      fromContext: z.boolean(),
    }),
  ),
  counts: z.object({
    covering: z.number().int(),
    expiringSoon: z.number().int(),
    expired: z.number().int(),
    awaitingSignature: z.number().int(),
  }),
});

export const createWaiverSchema = z.object({
  projectId: cuid,
  auditId: cuid.nullable(),
  itemRef: itemRef,
  reason: z.string().min(10).max(4000),
  expiresAt: isoDateTime.nullable(),
  /** The requester signs on submit, in this role. */
  signAsRole: teamRoleSchema,
});

export const signWaiverSchema = z.object({ role: teamRoleSchema });

export type Waiver = z.infer<typeof waiverSchema>;
export type Waivers = z.infer<typeof waiversSchema>;
export type CreateWaiverInput = z.infer<typeof createWaiverSchema>;

// ── Go-live gate & sign-off (§9.11, Phase 3) ──────────────────────────────────

export const gateDecisionSchema = z.object({
  projectId: cuid,
  projectName: z.string(),
  auditId: cuid.nullable(),
  auditNumber: z.number().int().nullable(),
  /**
   * The 22 questions and their verdicts. Deliberately NOT `gateSchema`: that one carries
   * its own `signOffs` array, and nesting it here would put the sheet in two places at
   * once — which is exactly how two copies of the same fact drift apart.
   */
  gate: z.object({
    rows: z.array(gateRowSchema),
    passing: z.number().int(),
    total: z.number().int(),
    ready: z.boolean(),
  }),
  /** Every blocking reason, in the words the sign-off sheet will show. */
  blockers: z.array(z.object({ kind: z.string(), detail: z.string(), refs: z.array(itemRef) })),
  signOffs: z.array(
    z.object({
      role: teamRoleSchema,
      required: z.boolean(),
      signedBy: z
        .object({ id: cuid, displayName: z.string(), initials: z.string() })
        .nullable(),
      signedAt: isoDateTime.nullable(),
      statement: z.string().nullable(),
    }),
  ),
  /** True only when every gate question passes and every required role has signed. */
  ready: z.boolean(),
  declaredAt: isoDateTime.nullable(),
});

export const signGateSchema = z.object({
  role: teamRoleSchema,
  statement: z.string().min(4).max(1000),
});

export type GateDecision = z.infer<typeof gateDecisionSchema>;
